/**
 * Created by: Pranab Dhar
 */
import {Observable, Subject} from "rxjs";

import {HANDLER_FUNCTION} from "../../types";

export let hasOwnProp = {}.hasOwnProperty;

/**
 * Asynchronous Messaging interface
 *
 * @description The messaging interface provides a publisher-subscriber broadcastubg servuce where one
 *  side publishes data and other sides subscribe to the data.
 */
export interface IMessaging {
    /**
     * Object for holding subjects as properties
     */
    Subjects: any;

    /**
     * @description Creates a new Subject
     * @return {Subject<T>}
     */
    newSubject<T>(): Subject<T>;

    /**
     * @description Returns an Observable for the given subject name
     * @param {string} name
     * @return {Observable<T>}
     */
    getObservable<T>(name: string): Observable<T>;

    /**
     * @description Emit data of type {T} for a given subject name
     * @param {string} subject name
     * @param {T} data
     */
    emit<T>(name: string, data: T);

    /**
     * Handle data of type {T} for a given subject name
     * @param {string} subject name
     * @param {HANDLER_FUNCTION<T>} handler
     */
    on<T>(name: string, handler: HANDLER_FUNCTION<T>): void;

    /**
     * Delete the subject from the messaging subjects
     * @param {string} name
     * @param {HANDLER_FUNCTION<T>} handler
     */
    off<T>(name: string): void;

    /**
     * Dispose all the subjects and reinitialize the {Subjects} Object
     */
    dispose();
}

/**
 * Abstract Messaging implementation
 * @description There can be different messaging use cases where different type of Subjects can be used
 * i.e. {Rx.Subject} or {Rx.ReplaySubject} which determines what behavior is required for the use case.
 *
 * @example:
 * ```
 *  public class TestMessaging extends AbstractMessaging {
 *   static fromSubjects(subjects: any): AppMessaging {
 *      return new ContribMessaging(subjects);
 *   }
 *
 *   constructor(subjects?: any) {
 *       super(subjects);
 *   }
 *
 *   public newSubject<T>(): Subject<T> {
 *       return new ReplaySubject<T>();
 *   }
 *  }
 * ```
 */
export abstract class AbstractMessaging implements IMessaging {
    subjects: any = {};

    constructor(subjects?: any) {
        if (subjects) {
            this.subjects = subjects;
        }
    }

    abstract newSubject<T>(): Subject<T>;

    get Subjects(): any {
        return this.subjects;
    }

    set Subjects(subjects: any) {
        this.subjects = subjects;
    }

    getObservable<T>(name: string): Observable<T> {
        if (!this.subjects[name]) {
            const fnName = this.createName(name);

            // tslint:disable-next-line:no-unused-expression
            this.subjects[fnName] || (this.subjects[fnName] = this.newSubject<T>());
        }
        return Observable.from(this.subjects["$" + name]);
    }

    createName(name: string): string {
        return "$" + name;
    }

    emit<T>(name: string, data: T) {
        const fnName = this.createName(name);

        // tslint:disable-next-line:no-unused-expression
        this.subjects[fnName] || (this.subjects[fnName] = this.newSubject<T>());
        this.subjects[fnName].next(data);
    }

    on<T>(name: string, handler: HANDLER_FUNCTION<T>): void {
        const fnName = this.createName(name);
        // tslint:disable-next-line:no-unused-expression
        this.subjects[fnName] || (this.subjects[fnName] = this.newSubject());
        this.subjects[fnName].subscribe(handler);
    }

    off<T>(name: string): void {
        const fnName = this.createName(name);
        if (this.subjects[fnName]) {
            this.subjects[fnName].unsubscribe();
            delete this.subjects[fnName];
        }
    }

    dispose(name?: string) {
        const subjects = this.subjects;
        if (name) {
            if (hasOwnProp.call(subjects, "$" + name)) {
                subjects["$" + name].unsubscribe();
                delete subjects["$" + name];
            }
        } else {
            for (const prop in subjects) {
                if (hasOwnProp.call(subjects, prop) && typeof subjects[prop].unsubscribe === "function") {
                    subjects[prop].unsubscribe();
                }
            }
            this.subjects = {};
        }

    }
}


export class TCIAppMessaging extends AbstractMessaging {
    private static instance: TCIAppMessaging;

    static getInstance(subjects: any): TCIAppMessaging {
        if (!TCIAppMessaging.instance) {
            TCIAppMessaging.instance = new TCIAppMessaging(subjects);
        }
        return TCIAppMessaging.instance;
    }

    static fromSubjects(subjects: any): TCIAppMessaging {
        return new TCIAppMessaging(subjects);
    }

    public newSubject<T>(): Subject<T> {
        return new Subject<T>();
    }

    constructor(subjects?: any) {
        super(subjects);
    }
}


// export class NgAppMessaging extends AbstractMessaging {
//     private static instance: NgAppMessaging;
//
//     static getInstance(): NgAppMessaging {
//         if (!NgAppMessaging.instance) {
//             NgAppMessaging.instance = new NgAppMessaging();
//         }
//         return NgAppMessaging.instance;
//     }
//
//     private constructor() {
//         super({});
//     }
//
//     public newSubject<T>(): Subject<T> {
//         return new Subject<T>();
//     }
// }
